import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 14);
  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
};

const formatDateTimeBR = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("pt-BR");
};

const formatDateBR = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pt-BR");
};

const formatDuration = (seconds) => {
  const safe = Math.max(0, Math.round(seconds));
  const min = Math.floor(safe / 60);
  const sec = safe % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const DiscountCampaignModal = ({ open, onClose, onSent }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const defaults = useMemo(() => getDefaultDates(), []);
  const [discountType, setDiscountType] = useState("Desconto 199");
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewName, setPreviewName] = useState("Nome do Cliente");

  const [previewData, setPreviewData] = useState(null);
  const [sendData, setSendData] = useState(null);
  const [previewEmailData, setPreviewEmailData] = useState(null);
  const [lastBatch, setLastBatch] = useState(null);
  const [sendingIndex, setSendingIndex] = useState(0);
  const [sendProgress, setSendProgress] = useState(0);
  const [error, setError] = useState("");

  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendPreviewEmailLoading, setSendPreviewEmailLoading] = useState(false);

  const payload = {
    discount_type: discountType,
    startDate,
    endDate,
  };

  const throttleLimit =
    previewData?.throttle?.limitEfetivo ||
    previewData?.throttle?.limitSolicitado ||
    0;
  const throttleIntervalMs = previewData?.throttle?.intervalMs || 0;
  const totalElegiveis = previewData?.totalElegiveis || 0;
  const selectedForThisBatch = throttleLimit
    ? Math.min(totalElegiveis, throttleLimit)
    : totalElegiveis;
  const estimatedSeconds = (selectedForThisBatch * throttleIntervalMs) / 1000;
  const currentSendingEmail =
    previewData?.amostra?.[sendingIndex]?.email || null;

  const requestJson = async (url, body) => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const raw = await response.text();
    let data = null;

    try {
      data = raw ? JSON.parse(raw) : {};
    } catch (_error) {
      const looksLikeHtml =
        raw.trim().startsWith("<!DOCTYPE") || raw.trim().startsWith("<html");
      if (looksLikeHtml) {
        throw new Error(
          "A API retornou HTML em vez de JSON. Reinicie o backend para carregar as novas rotas de campanha."
        );
      }
      throw new Error("Resposta inválida da API.");
    }

    if (!response.ok) {
      throw new Error(data?.message || "Falha na requisição.");
    }

    return data;
  };

  const loadLastBatch = async () => {
    try {
      const ts = Date.now();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/discount-campaign/last-batch?_=${ts}`,
        { cache: "no-store" }
      );
      const raw = await response.text();
      const data = raw ? JSON.parse(raw) : {};
      if (!response.ok) {
        throw new Error(data?.message || "Falha ao carregar status diário.");
      }
      setLastBatch(data);
    } catch (_error) {
      setError(
        "Não foi possível carregar 'Envios de hoje'. Verifique a configuração do backend."
      );
    }
  };

  useEffect(() => {
    if (open) {
      loadLastBatch();
    }
  }, [open]);

  useEffect(() => {
    if (!sendLoading) return;
    const hasEmails = Array.isArray(previewData?.amostra) && previewData.amostra.length > 0;
    if (!hasEmails) return;
    const timer = setInterval(() => {
      setSendingIndex((prev) => (prev + 1) % previewData.amostra.length);
    }, 1200);
    return () => clearInterval(timer);
  }, [sendLoading, previewData]);

  useEffect(() => {
    if (!sendLoading) return;
    if (!selectedForThisBatch || !throttleIntervalMs) return;
    const timer = setInterval(() => {
      setSendProgress((prev) => {
        if (prev >= selectedForThisBatch) return prev;
        return prev + 1;
      });
    }, throttleIntervalMs);
    return () => clearInterval(timer);
  }, [sendLoading, selectedForThisBatch, throttleIntervalMs]);

  const applyPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    setStartDate(formatDateInput(start));
    setEndDate(formatDateInput(end));
  };

  const handlePreview = async () => {
    setError("");
    setSendData(null);
    setPreviewLoading(true);

    try {
      const data = await requestJson(
        `${process.env.REACT_APP_API_BASE_URL}/users/discount-campaign/preview`,
        payload
      );
      setPreviewData(data);
    } catch (err) {
      setError(err.message || "Falha ao gerar prévia.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendPreviewEmail = async () => {
    setError("");
    setPreviewEmailData(null);
    setSendPreviewEmailLoading(true);

    try {
      const data = await requestJson(
        `${process.env.REACT_APP_API_BASE_URL}/users/discount-campaign/send-preview-email`,
        {
          discount_type: discountType,
          previewEmail,
          previewName,
        }
      );
      setPreviewEmailData(data);
    } catch (err) {
      setError(err.message || "Falha ao enviar e-mail de prévia.");
    } finally {
      setSendPreviewEmailLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    setError("");
    setSendingIndex(0);
    setSendProgress(0);
    setSendLoading(true);

    try {
      const data = await requestJson(
        `${process.env.REACT_APP_API_BASE_URL}/users/discount-campaign/send`,
        payload
      );
      setSendData(data);
      setSendProgress(data?.totalSelecionados || 0);
      loadLastBatch();
      if (typeof onSent === "function") onSent(data);
    } catch (err) {
      setError(err.message || "Falha ao enviar campanha.");
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "min(1100px, 96vw)",
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="div" variant="h6" sx={{ fontWeight: 700 }}>
          Campanha de Desconto
        </Typography>
        <Typography component="div" variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Filtre pelo período de cadastro dos usuários, valide o público e envie um e-mail de teste.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Público da Campanha
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Período baseado na data de cadastro do usuário.
              </Typography>

              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: "background.default" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Envios de hoje (limite diário)
                </Typography>
                {(() => {
                  const hasSentToday =
                    lastBatch && typeof lastBatch.sentToday === "number";
                  const hasDailyLimit =
                    lastBatch && typeof lastBatch.dailyLimit === "number";
                  const dailyText = hasSentToday
                    ? `${lastBatch.sentToday}/${hasDailyLimit ? lastBatch.dailyLimit : "-"}`
                    : "-/-";
                  return (
                    <Stack spacing={0.4}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{dailyText}</strong>
                      </Typography>
                      {hasSentToday && !hasDailyLimit ? (
                        <Typography variant="caption" color="warning.main">
                          Limite diário não configurado no backend
                        </Typography>
                      ) : null}
                      {!hasSentToday ? (
                        <Typography variant="caption" color="warning.main">
                          Configuração pendente no backend
                        </Typography>
                      ) : null}
                    </Stack>
                  );
                })()}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: "background.default" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Último lote enviado
                </Typography>
                {lastBatch?.hasData ? (
                  <Stack spacing={0.4}>
                    <Typography variant="body2" color="text.secondary">
                      {lastBatch.discount_type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cadastro: {formatDateBR(lastBatch.period?.startDate)} até{" "}
                      {formatDateBR(lastBatch.period?.endDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Último envio: {formatDateTimeBR(lastBatch.ultima_execucao_em)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enviados {lastBatch.enviados} • Falhas {lastBatch.falhas} • Total {lastBatch.total_processados}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum lote registrado ainda.
                  </Typography>
                )}
              </Paper>

              <TextField
                select
                label="Desconto"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                fullWidth
              >
                <MenuItem value="Desconto 199">Desconto 199</MenuItem>
                <MenuItem value="Desconto 179">Desconto 179</MenuItem>
                <MenuItem value="Desconto 149">Desconto 149</MenuItem>
              </TextField>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  type="date"
                  label="Cadastro de"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="date"
                  label="Cadastro até"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label="Últimos 7 dias" onClick={() => applyPreset(7)} />
                <Chip label="Últimos 15 dias" onClick={() => applyPreset(15)} />
                <Chip label="Últimos 30 dias" onClick={() => applyPreset(30)} />
              </Stack>

              <Button
                onClick={handlePreview}
                variant="outlined"
                disabled={previewLoading || sendLoading || sendPreviewEmailLoading}
              >
                {previewLoading ? "Consultando público..." : "Ver Prévia Público"}
              </Button>

              {previewData ? (
                <Stack spacing={1}>
                  <Alert severity="info">
                    Elegíveis no período de cadastro: <strong>{previewData.totalElegiveis}</strong>
                  </Alert>
                  {throttleLimit ? (
                    <Alert severity="warning">
                      Limite por disparo: <strong>{throttleLimit}</strong>.{" "}
                      {previewData.totalElegiveis > throttleLimit
                        ? "Este envio processará apenas um lote; os demais ficam para o próximo disparo."
                        : "Todo o público elegível será processado neste disparo."}
                    </Alert>
                  ) : null}
                  {throttleLimit && throttleIntervalMs ? (
                    <Alert severity="info">
                      Estimativa deste lote: <strong>{selectedForThisBatch}</strong> envio(s) em{" "}
                      <strong>{formatDuration(estimatedSeconds)}</strong>.
                    </Alert>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Clique em "Ver Prévia Público" para conferir quantas pessoas receberão com base no cadastro.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                E-mail de Prévia
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Envie uma amostra para seu e-mail e valide o resultado real na caixa de entrada.
              </Typography>

              <TextField
                label="Nome na prévia"
                value={previewName}
                onChange={(e) => setPreviewName(e.target.value)}
                fullWidth
              />
              <TextField
                label="E-mail de teste"
                value={previewEmail}
                onChange={(e) => setPreviewEmail(e.target.value)}
                placeholder="seuemail@dominio.com"
                fullWidth
              />

              <Button
                onClick={handleSendPreviewEmail}
                variant="outlined"
                disabled={sendPreviewEmailLoading || previewLoading || sendLoading}
              >
                {sendPreviewEmailLoading
                  ? "Enviando e-mail de prévia..."
                  : "Enviar E-mail de Prévia"}
              </Button>

              {previewEmailData ? (
                <Alert severity="success">
                  Prévia enviada para <strong>{previewEmailData.to}</strong>
                </Alert>
              ) : null}

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Disparo da Campanha
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleSendCampaign}
                  disabled={sendLoading || previewLoading || sendPreviewEmailLoading}
                >
                  {sendLoading ? "Enviando campanha..." : "Enviar Campanha"}
                </Button>
              </Box>

              {sendData ? (
                <Stack spacing={1}>
                  {sendData.totalSelecionados === 0 ? (
                    <Alert severity="info">
                      Nenhum envio pendente para esse período. Parece que esse público já foi processado.
                    </Alert>
                  ) : (
                    <Alert severity="success">
                      Lote concluído. Selecionados: {sendData.totalSelecionados} | Enviados:{" "}
                      {sendData.enviados} | Falhas: {sendData.falhas}
                    </Alert>
                  )}

                  {previewData &&
                  sendData.totalSelecionados > 0 &&
                  previewData.totalElegiveis > sendData.totalSelecionados ? (
                    <Alert severity="warning">
                      Ainda restam contatos fora deste lote. Refaça o processo para finalizar o período completo.{" "}
                      <strong>
                        Restantes estimados:{" "}
                        {Math.max(
                          previewData.totalElegiveis - sendData.totalSelecionados,
                          0
                        )}
                      </strong>
                    </Alert>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </Paper>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        {sendLoading ? (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "background.default",
            }}
          >
            <CircularProgress size={24} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Enviando e-mails, aguarde até o final
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Não feche esta tela durante o processamento do lote.
              </Typography>
              {selectedForThisBatch > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Progresso estimado:{" "}
                  <strong>
                    {Math.min(sendProgress, selectedForThisBatch)}/{selectedForThisBatch}
                  </strong>
                </Typography>
              ) : null}
              {currentSendingEmail ? (
                <Typography variant="body2" color="text.secondary">
                  Enviando e-mail para: <strong>{currentSendingEmail}</strong>
                </Typography>
              ) : null}
            </Box>
          </Paper>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscountCampaignModal;
